import { Link, createFileRoute } from '@tanstack/react-router'
import { MeetingBackfillHistory } from './-components/MeetingBackfillHistory'
import { MeetingBackfillPanel } from './-components/MeetingBackfillPanel'
import { MeetingDetailSheet } from './-components/MeetingDetailSheet'
import { MeetingTranscriptViewer } from './-components/MeetingTranscriptViewer'
import { MeetingsTab } from './-components/MeetingsTab'
import { OutcomesTab } from './-components/OutcomesTab'
import { OverviewTab } from './-components/OverviewTab'
import { ZoomWebhookEvents } from './-components/ZoomWebhookEvents'
import type { MEETING_TRANSCRIPTS_TABS } from '@/lib/meeting-transcripts-search'
import { PageTitle } from '@/components/PageTitle'
import { BrandLogo } from '@/components/shared/BrandLogo'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/animate-ui/components/radix/tabs'
import { Fade } from '@/components/animate-ui/primitives/effects/fade'
import {
  useMeetingTranscriptsStatus,
  useRefreshWhenBackfillFinishes,
} from '@/hooks/repositories/useMeetingTranscriptsRepository'
import { useMeetingSheetHistory } from '@/hooks/use-cases/useMeetingSheetHistory'
import { useMeetingTranscriptsNavigation } from '@/hooks/use-cases/useMeetingTranscriptsNavigation'
import { requirePermission } from '@/lib/permissions/route-guards'
import { BACKOFFICE_PERMISSION_KEYS } from '@/lib/permissions/backoffice-permissions'
import { meetingTranscriptsSearchSchema } from '@/lib/meeting-transcripts-search'

type MeetingTranscriptsTab = (typeof MEETING_TRANSCRIPTS_TABS)[number]

export const Route = createFileRoute(
  '/_app/backoffice/integrations/meeting-transcripts/',
)({
  validateSearch: meetingTranscriptsSearchSchema,
  component: RouteComponent,
  beforeLoad: requirePermission(BACKOFFICE_PERMISSION_KEYS.meetingTranscripts),
})

function RouteComponent() {
  const { tab, meetingId, transcriptId, q } = Route.useSearch()

  // Already polled while a backfill runs; here it only tells the lists when
  // that backfill is over, so the meetings it brought in appear.
  const status = useMeetingTranscriptsStatus()
  useRefreshWhenBackfillFinishes(status.data)

  const { changeTab, openBackfill, openNeedsMeeting, openCalls, openOutcomes } =
    useMeetingTranscriptsNavigation()

  // Opening pushes a history entry, so Back closes the sheet; closing from the
  // page pops it again, so the history ends where Back would have left it.
  const { openMeeting, closeMeeting, openTranscript, closeTranscript } =
    useMeetingSheetHistory(transcriptId)

  return (
    <div className="py-4">
      <PageTitle title="Meeting transcripts" />

      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/backoffice">Backoffice</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/backoffice/integrations">Integrations</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Meeting transcripts</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="mb-4 flex items-center gap-3">
        <div className="flex -space-x-2">
          <BrandLogo
            domain="zoom.us"
            name="Zoom"
            className="size-8 ring-2 ring-background"
          />
          <BrandLogo
            domain="avoma.com"
            name="Avoma"
            className="size-8 ring-2 ring-background"
          />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Meeting transcripts
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Calls recorded by Zoom and Avoma, each call shown once with its
            transcripts.
          </p>
        </div>
      </div>

      <Fade transition={{ duration: 0.2 }}>
        <Tabs
          value={tab}
          onValueChange={(value) => changeTab(value as MeetingTranscriptsTab)}
          className="w-full"
        >
          <TabsList className="h-11 w-full sm:h-9 sm:w-fit">
            {/* Five equal tabs fit a phone only with the short word and a
                tighter label: below `sm` the triggers are `text-xs px-1`. */}
            <TabsTrigger
              value="overview"
              className="max-sm:px-1 max-sm:text-xs sm:min-w-28"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="meetings"
              className="max-sm:px-1 max-sm:text-xs sm:min-w-28"
            >
              Meetings
            </TabsTrigger>
            <TabsTrigger
              value="outcomes"
              className="max-sm:px-1 max-sm:text-xs sm:min-w-28"
            >
              Outcomes
            </TabsTrigger>
            <TabsTrigger
              value="backfill"
              className="max-sm:px-1 max-sm:text-xs sm:min-w-28"
            >
              Backfill
            </TabsTrigger>
            <TabsTrigger
              value="webhooks"
              aria-label="Zoom webhooks"
              className="max-sm:px-1 max-sm:text-xs sm:min-w-36"
            >
              <span className="sm:hidden">Webhooks</span>
              <span className="hidden sm:inline">Zoom webhooks</span>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4 w-full">
            <OverviewTab
              onOpenBackfill={openBackfill}
              onOpenNeedsMeeting={openNeedsMeeting}
              onOpenCalls={openCalls}
              onOpenOutcomes={openOutcomes}
            />
          </TabsContent>
          <TabsContent value="meetings" className="mt-4 w-full">
            <MeetingsTab
              selectedMeetingId={meetingId}
              onSelectMeeting={openMeeting}
            />
          </TabsContent>
          <TabsContent value="outcomes" className="mt-4 w-full">
            <OutcomesTab onViewCall={openMeeting} />
          </TabsContent>
          <TabsContent value="backfill" className="mt-4 w-full">
            <div className="space-y-4">
              <MeetingBackfillPanel />
              <MeetingBackfillHistory />
            </div>
          </TabsContent>
          <TabsContent value="webhooks" className="mt-4 w-full">
            <ZoomWebhookEvents />
          </TabsContent>
        </Tabs>
      </Fade>

      {/* Driven by the URL, so a meeting or a transcript can be linked. The
          transcript sheet opens over the meeting that led to it. */}
      <MeetingDetailSheet
        meetingId={meetingId}
        onClose={closeMeeting}
        onViewTranscript={openTranscript}
      />
      {/* Opened from a search, the transcript lights up what was searched. */}
      <MeetingTranscriptViewer
        transcriptId={transcriptId}
        searchQuery={q}
        onClose={closeTranscript}
      />
    </div>
  )
}
